import React, { useState, useRef, useEffect } from 'react';
import { Routes, Route, useParams } from 'react-router-dom';
import YouTubePlayer from './YouTubePlayer';
import VideoOverlay from './VideoOverlay';
import './App.css';
import './Overlay.css';
import { fetchLiveVideos } from './liveFetcher';

const TOTAL_PLAYERS = 4;
const HARDCODED_FALLBACK_VIDEOS = ['OlVfPZ4Bntc', 'rpbdSaczhIM', 'OY769Hindhk', 'DRY1Qu5mJ-s', 'hbS_hhxi8Ts'];
const PRE_RECORDED_NAMES = new Set(['thenomadandy', 'FollowAlwaysDisneyWorld', 'WorldScape', 'GeoffreyNease', 'WDW247', 'goodenjoymenttv']);
const BLOCKED_VIDEO_IDS = new Set(['q6mmNbQ-krs']);

function MainGrid() {
  const [videoIds, setVideoIds] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [playersReadyCount, setPlayersReadyCount] = useState(0);
  const [showOverlay, setShowOverlay] = useState(false);
  const [activeOverlayIndex, setActiveOverlayIndex] = useState(null);
  const [liveStreams, setLiveStreams] = useState({});
  const [endedIndexes, setEndedIndexes] = useState([]);
  const [fullscreenIndex, setFullscreenIndex] = useState(null);
  const [chatVisibleIndex, setChatVisibleIndex] = useState(null);
  const [autoplayUnlocked, setAutoplayUnlocked] = useState(false);
  const [currentVideoIds, setCurrentVideoIds] = useState([]);
  const lastEnterTime = useRef(0);
  const playersRef = useRef([]);

  const videoIdToStreamer = Object.entries(liveStreams).reduce((acc, [streamer, { live }]) => {
    if (live?.videoId) acc[live.videoId] = streamer;
    return acc;
  }, {});

  const isInUse = (videoId) => currentVideoIds.includes(videoId);

  useEffect(() => {
    const fetchAndInitialize = async () => {
      try {
        const liveMap = await fetchLiveVideos();
        setLiveStreams(liveMap);
        const liveInParks = [];
        const loopStreams = [];

        for (const [name, { live }] of Object.entries(liveMap)) {
          if (!live?.videoId || BLOCKED_VIDEO_IDS.has(live.videoId)) continue;
          if (PRE_RECORDED_NAMES.has(name)) loopStreams.push(live.videoId);
          else liveInParks.push(live.videoId);
        }

        const combined = [...liveInParks, ...loopStreams, ...HARDCODED_FALLBACK_VIDEOS].filter(
          (id) => !BLOCKED_VIDEO_IDS.has(id)
        );

        setVideoIds(combined.slice(0, TOTAL_PLAYERS));
        setCurrentVideoIds(combined.slice(0, TOTAL_PLAYERS));
      } catch {
        const fallback = shuffleArray(HARDCODED_FALLBACK_VIDEOS).slice(0, TOTAL_PLAYERS);
        setVideoIds(fallback);
        setCurrentVideoIds(fallback);
      }
    };

    fetchAndInitialize();
  }, []);

  useEffect(() => {
    if (playersReadyCount === TOTAL_PLAYERS) {
      playersRef.current.forEach((player, i) => {
        if (player && player.mute && player.unMute && player.playVideo) {
          if (!autoplayUnlocked) {
            player.mute();
          } else {
            if (i === selectedIndex) {
              player.unMute();
              player.playVideo();
            } else {
              player.mute();
            }
          }
        }
      });
    }
  }, [selectedIndex, playersReadyCount, autoplayUnlocked]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showOverlay) return;

      const now = Date.now();
      const isDoubleEnter = e.key === 'Enter' && now - lastEnterTime.current < 800;

      if (!autoplayUnlocked) setAutoplayUnlocked(true);

      const itemsPerRow = 2;
      let newIndex = selectedIndex;

      const getRowAndCol = (index) => {
        return {
          row: Math.floor(index / itemsPerRow),
          col: index % itemsPerRow,
        };
      };

      if (e.key === 'Enter') {
        const now = Date.now();
        const isDoubleEnter = now - lastEnterTime.current < 400;
      
        lastEnterTime.current = now;
      
        if (isDoubleEnter) {
          setFullscreenIndex((prev) => (prev === selectedIndex ? null : selectedIndex));
          setChatVisibleIndex(null);
        } else {
          setTimeout(() => {
            if (Date.now() - lastEnterTime.current >= 400) {
              handleVideoClick(selectedIndex);
            }
          }, 400);
        }
      
        return;
      }
      

      switch (e.key) {
        case 'ArrowUp': {
          const { row, col } = getRowAndCol(selectedIndex);
          if (row > 0) newIndex = (row - 1) * itemsPerRow + col;
          break;
        }
        case 'ArrowDown': {
          const { row, col } = getRowAndCol(selectedIndex);
          if (row < 1) newIndex = (row + 1) * itemsPerRow + col;
          break;
        }
        case 'ArrowLeft':
          if (selectedIndex % itemsPerRow !== 0) newIndex = selectedIndex - 1;
          break;
        case 'ArrowRight':
          if (selectedIndex % itemsPerRow !== itemsPerRow - 1) newIndex = selectedIndex + 1;
          break;
        default: return;
      }

      if (newIndex >= 0 && newIndex < videoIds.length) {
        setSelectedIndex(newIndex);
      }

      e.preventDefault();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, showOverlay, autoplayUnlocked, videoIds.length]);

  const handlePlayerReady = (index, player) => {
    playersRef.current[index] = player;
    setPlayersReadyCount((prev) => prev + 1);
  };

  const handleVideoEnd = (index) => {
    setEndedIndexes((prev) => [...prev, index]);
  };

  const handleVideoClick = async (index) => {
    setActiveOverlayIndex(index);
    setShowOverlay(true);
    try {
      const liveMap = await fetchLiveVideos();
      setLiveStreams(liveMap);
    } catch (err) {
      console.error('❌ Failed to fetch live streams when opening modal:', err);
    }
  };

  const handleVideoSelect = (newVideoId) => {
    const updated = [...videoIds];
    updated[activeOverlayIndex] = newVideoId;
    setVideoIds(updated);
    setCurrentVideoIds(updated);
    setEndedIndexes((prev) => prev.filter((i) => i !== activeOverlayIndex));
    setShowOverlay(false);

    const player = playersRef.current[activeOverlayIndex];
    if (player?.loadVideoById) {
      player.loadVideoById(newVideoId);
      setTimeout(() => {
        playersRef.current.forEach((p, i) => {
          if (p && p.mute && p.unMute && p.playVideo) {
            if (i === activeOverlayIndex) {
              p.unMute();
              p.playVideo();
            } else {
              p.mute();
            }
          }
        });
      }, 1000);
    }
  };

  return (
    <div className={`grid ${fullscreenIndex !== null ? 'fullscreen-active' : ''}`}>
      {videoIds.map((id, index) => {
        const isLive = Object.values(liveStreams).some((s) => s.live?.videoId === id);
        const streamerName = videoIdToStreamer[id] || '';
        const isFullscreen = fullscreenIndex === index;
        const isChatVisible = chatVisibleIndex === index;

        return (
          <div
            key={index}
            className={`video-wrapper ${selectedIndex === index ? 'selected' : ''} ${isFullscreen ? 'fullscreen' : ''}`}
          >
            {endedIndexes.includes(index) ? (
              <div className="add-video-button" onClick={() => handleVideoClick(index)}>
                <div className="plus-sign">+</div>
                <div className="add-label">Add Video</div>
              </div>
            ) : (
              <>
                <YouTubePlayer
                  videoId={id}
                  onPlayerReady={(player) => handlePlayerReady(index, player)}
                  onEnd={() => handleVideoEnd(index)}
                />
                {streamerName && PRE_RECORDED_NAMES.has(streamerName) ? (
                  <div className="pre-recorded-badge">Previously Recorded</div>
                ) : isLive ? (
                  <div className="live-badge">LIVE</div>
                ) : null}
                {streamerName && <div className="streamer-label">@{streamerName}</div>}
                {isChatVisible && (
                  <div className="chat-container">
                    <iframe
                      src={`https://www.youtube.com/live_chat?v=${id}&embed_domain=${window.location.hostname}`}
                      title="YouTube Chat"
                      frameBorder="0"
                      allowFullScreen
                    />
                  </div>
                )}
                <div className="click-blocker" onClick={() => handleVideoClick(index)} />
              </>
            )}
          </div>
        );
      })}

      {showOverlay && (
        <VideoOverlay
          onSelect={handleVideoSelect}
          onClose={() => setShowOverlay(false)}
          liveStreams={liveStreams}
          currentVideoIds={videoIds}
        />
      )}
    </div>
  );
}

function ChatPage() {
  const { videoId } = useParams();
  const domain = window.location.hostname;
  const chatUrl = `https://www.youtube.com/live_chat?v=${videoId}&embed_domain=${domain}`;

  return (
    <div style={{ width: '100vw', height: '100vh', background: 'black' }}>
      <iframe
        src={chatUrl}
        title="YouTube Chat"
        width="100%"
        height="100%"
        frameBorder="0"
        allowFullScreen
      ></iframe>
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<MainGrid />} />
      <Route path="/chat/:videoId" element={<ChatPage />} />
    </Routes>
  );
}

function shuffleArray(array) {
  return [...array].sort(() => Math.random() - 0.5);
}

export default App;